import React, {
  useContext,
  useRef,
} from 'react';
import Table from 'core/components/VirtualTable';
import TableRow from 'core/components/VirtualTable/Row';
import FileDragOverlayProvider from 'core/context/fileDragOverlay';
import {
  Row,
} from '@tanstack/react-table';
import {
  useFileDragOverlayContext,
  useNotify,
  useSelectableContext,
} from 'core/hooks';
import clsx from 'clsx';
import {
  MultimediaTypeWithCategory,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia/FileUploadDialog';
import {
  mediaEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  toLocalUrls,
} from 'utils/files';
import {
  columns,
  FileUploadDialogContext,
} from 'pages/Dashboard/pages/Charts/pages/Details/components/Multimedia/FileUploadDialog/columns';
import isNil from 'lodash/isNil';
import UploadIcon from 'core/icons/upload';

// Keep this in sync with the row min-height so fixed virtualization does not jitter.
const mediaTypeListRowHeight = 40;
const mediaTypeListRowClassName = '!py-1 !min-h-[40px]';

type Props = {
  data: MultimediaTypeWithCategory[];
  dropzoneAccept?: Record<string, string[]>;
  disabled?: boolean;
};

type MediaTypeRowProps = {
  row: Row<MultimediaTypeWithCategory>;
  disabled?: boolean;
};

function MediaTypeRow({ row, disabled = false }: MediaTypeRowProps) {
  const context = useFileDragOverlayContext();

  const { isSelected, toggle } = useSelectableContext();
  const { original } = row;
  const { label, files = [] } = original;
  const isRowSelected = isSelected(label ?? '');

  return (
    <TableRow<MultimediaTypeWithCategory>
      row={row}
      className={clsx(mediaTypeListRowClassName, {
        'bg-blue-50 opacity-90': context?.isDragging && isNil(context?.rejectReason),
        'bg-red-50 opacity-70': context?.isError,
      })}
      onClick={() => {
        if (!disabled && files.length > 0 && !isRowSelected) {
          toggle(label ?? '');
        }
      }}
    />
  );
}

type DroppableRowProps = {
  row: Row<MultimediaTypeWithCategory>;
  dropzoneAccept?: Record<string, string[]>;
  disabled?: boolean;
};

function DroppableRow({ row, dropzoneAccept, disabled = false }: DroppableRowProps) {
  const { isLimitReached } = useContext(FileUploadDialogContext);
  const rowRef = useRef<HTMLDivElement | null>(null);
  const notify = useNotify();
  return (
    <div ref={rowRef} className="relative w-full h-full overflow-hidden">
      <FileDragOverlayProvider
        targetRef={rowRef}
        disabled={disabled}
        onDropFile={async (file: File | File[]) => {
          if (!isLimitReached) {
            const localFile = await toLocalUrls(Array.isArray(file) ? file : [file]);
            mediaEventChannel.emit('onMediaUploadedToCategory', {
              files: [
                ...(row.original?.files ?? []),
                ...localFile,
              ],
              original: row.original,
            });
          }
        }}
        onReject={(reason) => {
          if (reason === 'type') {
            notify({ variant: 'error', message: 'This type of file is not supported.' });
          }
        }}
        messages={{
          default: 'Drop your media file to select',
          uploading: 'Uploading media...',
          supportedHint: '',
        }}
        backgroundClassName="bg-white/80"
        dropzoneAccept={dropzoneAccept}
        multiple
        animate
        renderOverlayContent={({ isError, message }) => (
          <div className="relative flex flex-row items-center gap-2 px-2 py-1 bg-white/60">
            <UploadIcon className={clsx(
              '!w-5 !h-5',
              { 'text-red-500': isError || isLimitReached, 'text-blue-500': !isError && !isLimitReached },
            )}
            />
            <div className="flex flex-col items-start text-left">
              <div className={clsx('text-xs font-semibold', isError ? 'text-red-700' : 'text-gray-800')}>
                {isLimitReached
                  ? 'Maximum 10 files can be uploaded at a time.'
                  : message}
              </div>
            </div>
          </div>
        )}
      >
        <MediaTypeRow
          row={row}
          disabled={disabled}
        />
      </FileDragOverlayProvider>
    </div>
  );
}

export default function MediaTypesList({
  data,
  dropzoneAccept,
  disabled = false,
}: Props) {
  return (
    <div className="w-full h-full min-h-[6rem] rounded-lg overflow-auto">
      <Table<MultimediaTypeWithCategory>
        data={data}
        columns={columns}
        className="!h-full"
        classes={{
          outerHeader: 'hidden',
          header: 'hidden',
          row: 'hover:!border-none',
          scrollContainer: '!pb-0',
          outerRow: '!px-1 !border-b-gray-100 hover:!border-populate-border hover:bg-gray-50 hover:!border-r-transparent hover:!border-l-transparent hover:!rounded-none hover:!shadow-none first:!border-t-0 last:!border-b-0',
        }}
        renderRowComponent={(row) => (
          <DroppableRow
            row={row}
            dropzoneAccept={dropzoneAccept}
            disabled={disabled}
          />
        )}
        itemSize={mediaTypeListRowHeight}
      />
    </div>
  );
}
